import { differenceBy, get, orderBy, uniqBy } from 'lodash'
import { bookingStatus } from 'pawpaddock-common/data/status'
import { set } from 'vue'

import { ApiModel } from '~/plugins/api/model'
import { dateNew } from '~/plugins/date'

const initialState = () => {
  return {
    basket: {},
    fetchBasketApi: new ApiModel(),
    bookings: [],
    previousBookings: [],
    fetchBookingsApi: new ApiModel(),
    fetchPreviousBookingsApi: new ApiModel(),
    creditBalance: 0,
    creditBlocks: [],
    fetchCreditBalanceApi: new ApiModel(),
    fetchCreditBlocksApi: new ApiModel(),
    verifyOrderApi: new ApiModel(),
    isPurchasing: false,
    isVerifying: false,
    verifyOrderError: null,
    retriedPaymentAt: null
  }
}

export const state = () => initialState()

export const getters = {
  getBasket: state => {
    // Group by paddock id
    const basket = { bookings: {}, vouchers: [] }

    basket.bookings = state.basket.bookings.reduce((acc, booking) => {
      const key = booking.paddock.name

      if (!acc[key]) {
        acc[key] = []
      }

      acc[key].push({ ...booking, basketType: 'booking' })

      // Order by startAt
      acc[key] = orderBy(acc[key], ['startAt'], ['asc'])

      return acc
    }, {})

    // Add vouchers
    state.basket.vouchers.forEach(voucher => {
      basket.vouchers.push({ ...voucher, basketType: 'voucher' })
    })

    return basket
  },

  totalItemsInBasket: state => {
    return 'bookings' in state.basket ? state.basket.bookings.length + state.basket.vouchers.length : 0
  },

  totalBookingsInBasket: state => {
    return 'bookings' in state.basket ? state.basket.bookings.length : 0
  },

  totalPriceInBasket: state => {
    const bookingValue = state.basket?.bookings.reduce((acc, item) => {
      return acc + item.price
    }, 0)

    const voucherValue = state.basket?.vouchers.reduce((acc, item) => {
      return acc + item.value
    }, 0)

    return bookingValue + voucherValue
  },

  getActiveBookings: state => {
    return state.bookings
      .filter(
        booking =>
          dateNew.fromSQL(booking.startAt) > dateNew.now().startOf('day') &&
          booking.status === bookingStatus.CONFIRMED
      )
      .sort((a, b) => dateNew.fromSQL(a.startAt).diff(dateNew.fromSQL(b.startAt)).toObject().milliseconds)
  },

  bookingRunningNow: (state, getters) => {
    return getters.getActiveBookings.find(booking => {
      const startAt = dateNew.fromSQL(booking.startAt)
      const endAt = dateNew.fromSQL(booking.endAt).plus({ minutes: 10 })

      return dateNew.now() >= startAt && dateNew.now() <= endAt
    })
  },

  getPreviousBookings: state => {
    // Get all bookings before yesterday
    return state.previousBookings || []
  },

  hasFailedToCheckout: (state, getters) => {
    return state.verifyOrderError !== null
  },

  totalActiveBookings: (state, getters) => {
    return getters.getActiveBookings.length
  },

  totalPreviousBookings: (state, getters) => {
    return getters.getPreviousBookings.length
  },

  creditBalance: state => {
    return state.creditBalance
  },

  hasCreditBalance: (state, getters) => {
    return getters.creditBalance > 0
  },

  lastBookedPaddock: (state, getters) => {
    return orderBy(state.bookings, 'createdAt', 'desc')[0]?.paddock
  },

  previousPaddocks: state => {
    let previousPaddocks = []

    // Get unique paddock names from previous bookings
    state.bookings.forEach(booking => {
      previousPaddocks.push(booking.paddock)
    })

    // Remove duplicates
    previousPaddocks = uniqBy(previousPaddocks, 'id')

    return previousPaddocks
  }
}

export const actions = {
  async fetchBasket({ state, commit }) {
    try {
      if (state.isPurchasing || state.isVerifying || state.verifyOrderError) {
        this.$log.debug('Skipping fetchBasket due to ongoing purchase or verification')
        return false
      }

      await this.$api.booking(state.fetchPaddocksApi).useStorePath('booking.fetchBasketApi').getBasket()

      commit('setBasket', { basket: get(state.fetchBasketApi, 'response.data', {}) })
    } catch (error) {
      this.$log.debug('Error fetching basket', error)
    }
  },

  resetBasket({ commit }) {
    this.$log.debug('Resetting basket')

    commit('setBasket', { basket: {}, checkForRemovals: false })
    commit('setIsVerifying', false)
    commit('setVerifyOrderError', null)
    commit('setIsPurchasing', false)
  },

  async fetchRecentBookings({ state, commit, rootGetters }) {
    if (!rootGetters['app/getApp'].isAuth) {
      return false
    }

    try {
      await this.$api.booking(state.fetchBookingsApi).useStorePath('booking.fetchBookingsApi').getRecentBookings()

      commit('setBookings', get(state.fetchBookingsApi, 'response.data', []))
    } catch (error) {
      this.$log.debug('Error fetching active bookings', error)
    }
  },

  async fetchPreviousBookings({ state, commit, rootGetters }) {
    if (!rootGetters['app/getApp'].isAuth) {
      return false
    }

    try {
      await this.$api
        .booking(state.fetchPreviousBookingsApi)
        .useStorePath('booking.fetchPreviousBookingsApi')
        .getPreviousBookings()

      commit('setPreviousBookings', get(state.fetchPreviousBookingsApi, 'response.data', []))
    } catch (error) {
      this.$log.debug('Error fetching previous bookings', error)
    }
  },

  async fetchCreditBalance({ state, commit }) {
    try {
      await this.$api
        .booking(state.fetchCreditBalanceApi)
        .useStorePath('booking.fetchCreditBalanceApi')
        .getCreditBalance()

      commit('setCreditBalance', state.fetchCreditBalanceApi.response.data?.balance || 0)
    } catch (error) {
      // STUB
    }
  },

  async fetchCreditBlocks({ state, commit }) {
    try {
      await this.$api
        .booking(state.fetchCreditBlocksApi)
        .useStorePath('booking.fetchCreditBlocksApi')
        .getCreditBlocks()

      const data = state.fetchCreditBlocksApi.response.data

      commit('setCreditBalance', data?.balance || 0)
      commit('setCreditBlocks', data?.blocks || [])
    } catch (error) {
      // STUB
    }
  }
}

export const mutations = {
  setBasket(state, { basket, checkForRemovals = true }) {
    const removedSessions = differenceBy(state.basket.bookings, basket.bookings, 'id')

    if (checkForRemovals && removedSessions.length > 0) {
      this.$modal.open('book/expired', removedSessions)
    }

    set(state, 'basket', basket)
  },

  setBookings(state, bookings) {
    set(state, 'bookings', bookings)
  },

  setPreviousBookings(state, bookings) {
    set(state, 'previousBookings', bookings)
  },

  setCreditBalance(state, balance) {
    set(state, 'creditBalance', balance)
  },

  setCreditBlocks(state, blocks) {
    set(state, 'creditBlocks', blocks)
  },

  setIsPurchasing(state, isPurchasing) {
    state.isPurchasing = isPurchasing
  },

  setIsVerifying(state, isVerifying) {
    state.isVerifying = isVerifying
  },

  setVerifyOrderError(state, error) {
    state.verifyOrderError = error
  },

  setRetriedPaymentAt(state, retriedPaymentAt) {
    state.retriedPaymentAt = retriedPaymentAt
  },

  reset(state) {
    this.$log.debug('Resetting basket module')

    Object.assign(state, initialState())
  }
}
